import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import Lottie from 'lottie-react';
import { X } from 'lucide-react';
import { isAnimatedStyle, loadWelcomeLottie } from '../../../data/welcomeStyles';

/**
 * Lazily loads + plays a welcome Lottie preset. Renders nothing until the
 * animation JSON resolves; calls `onUnavailable` if the preset can't load
 * so the caller can fall back to the classic layout.
 */
export function WelcomeLottie({ styleId, loop = true, className, onUnavailable }) {
    const [data, setData] = useState(null);

    useEffect(() => {
        let alive = true;
        setData(null);
        loadWelcomeLottie(styleId)
            .then((json) => { if (alive) setData(json); })
            .catch(() => { if (alive) onUnavailable?.(); });
        return () => { alive = false; };
    }, [styleId, onUnavailable]);

    if (!data) return null;
    return (
        <Lottie
            animationData={data}
            loop={loop}
            autoplay
            className={className}
            rendererSettings={{ preserveAspectRatio: 'xMidYMid meet' }}
        />
    );
}

/**
 * Optional marketing / announcement popup shown on first visit to a store
 * when `popup_title` or `popup_description` are set on the store.
 *
 * `style` selects the presentation:
 *   - 'classic' (default): the original static modal
 *   - a Lottie preset id: a friendly character animation greets the visitor
 *     above the message (falls back to classic if the asset fails to load).
 */
export function StorePopup({ isOpen, title, description, style, onClose }) {
    const [animated, setAnimated] = useState(isAnimatedStyle(style));

    useEffect(() => { setAnimated(isAnimatedStyle(style)); }, [style]);

    if (!isOpen || (!title && !description)) return null;

    return (
        <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="fixed inset-0 z-modal flex items-center justify-center p-4"
            role="dialog"
            aria-modal="true"
            aria-labelledby="store-popup-title"
        >
            <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.2 }}
                className="absolute inset-0 bg-ink/45 backdrop-blur-sm"
                onClick={onClose}
            />
            <motion.div
                initial={{ opacity: 0, scale: 0.95, y: 12 }}
                animate={{ opacity: 1, scale: 1, y: 0 }}
                exit={{ opacity: 0, scale: 0.96, y: 8 }}
                transition={{ type: 'spring', stiffness: 300, damping: 30 }}
                className="relative w-full max-w-lg surface-elevated rounded-2xl overflow-hidden max-h-[90vh] flex flex-col"
            >
                <button
                    onClick={onClose}
                    aria-label="סגור חלונית"
                    className="absolute top-3 end-3 w-9 h-9 inline-flex items-center justify-center rounded-md text-ink-subtle hover:text-ink hover:bg-surface-muted transition-colors z-10"
                >
                    <X className="w-5 h-5" />
                </button>

                {animated && (
                    <div className="bg-surface-muted/60 flex items-center justify-center pt-6">
                        <WelcomeLottie
                            styleId={style}
                            className="w-full max-w-sm h-48 sm:h-56"
                            onUnavailable={() => setAnimated(false)}
                        />
                    </div>
                )}

                <div className="px-8 pt-8 pb-6 text-center flex flex-col items-center overflow-y-auto flex-1">
                    {title && (
                        <motion.h2
                            id="store-popup-title"
                            className="heading-xl mb-4"
                            initial={animated ? { opacity: 0, y: 14 } : false}
                            animate={animated ? { opacity: 1, y: 0 } : {}}
                            transition={{ delay: 0.35, duration: 0.4 }}
                        >
                            {title}
                        </motion.h2>
                    )}
                    {description && (
                        <motion.p
                            className="text-ink-muted text-base sm:text-lg leading-relaxed whitespace-pre-line max-w-prose"
                            initial={animated ? { opacity: 0, y: 14 } : false}
                            animate={animated ? { opacity: 1, y: 0 } : {}}
                            transition={{ delay: 0.5, duration: 0.4 }}
                        >
                            {description}
                        </motion.p>
                    )}
                </div>
                <div className="px-8 pb-8 pt-4 text-center bg-surface border-t border-line-subtle">
                    <button
                        onClick={onClose}
                        className="px-6 py-3 rounded-xl bg-brand-500 hover:bg-brand-600 text-ink-inverse font-bold shadow-sm hover:shadow-md transition-[background-color,box-shadow,transform] duration-200 ease-smooth active:scale-[0.98]"
                    >
                        המשך לחנות
                    </button>
                </div>
            </motion.div>
        </motion.div>
    );
}

export default StorePopup;
